import { DemoBadge } from '../learning/DemoBadge'
import { useFocusOnMount } from '../learning/focus'
import { type SampleMaterial } from './model'
import { sourceLabel } from './report'
import { JOURNEY_STEPS, type JourneyStep, subjectOf } from './ui'

/**
 * Frame shared by every step of the School Material Learning Journey: back and
 * home actions, the step indicator and a focused heading (WCAG 2.4.3), the
 * always-visible source and plain DEMO notes.
 */

export interface JourneyFrameProps {
  readonly step: JourneyStep
  readonly title: string
  /** Previous step; null on the first step, where only "home" is offered. */
  readonly onBack: (() => void) | null
  readonly onHome: () => void
  readonly demo?: boolean
  readonly children: React.ReactNode
}

export function JourneyFrame({
  step,
  title,
  onBack,
  onHome,
  demo = false,
  children,
}: JourneyFrameProps): React.JSX.Element {
  const heading = useFocusOnMount<HTMLHeadingElement>()
  const index = JOURNEY_STEPS.indexOf(step)

  return (
    <div className="mx-auto flex w-full max-w-lg flex-1 flex-col gap-5 px-5 pt-1 pb-12 md:max-w-3xl">
      <div className="flex items-center justify-between gap-3">
        {onBack === null ? (
          <span />
        ) : (
          <button
            type="button"
            className="min-h-11 px-1 text-lg text-sky-800 underline dark:text-sky-300"
            onClick={onBack}
          >
            ← Quay lại
          </button>
        )}
        <button
          type="button"
          className="min-h-11 px-1 text-lg text-slate-700 underline dark:text-slate-300"
          onClick={onHome}
        >
          Về trang chính
        </button>
      </div>

      <nav aria-label="Các bước học từ tài liệu">
        <ol className="flex items-center gap-1.5">
          {JOURNEY_STEPS.map((s, i) => (
            <li
              key={s}
              aria-current={i === index ? 'step' : undefined}
              className="flex flex-1 flex-col items-center gap-1"
            >
              <span
                aria-hidden
                className={`h-2 w-full rounded-full ${
                  i <= index
                    ? 'bg-sky-600 dark:bg-sky-400'
                    : 'bg-slate-200 dark:bg-slate-800'
                }`}
              />
              <span
                className={`hidden text-sm md:block ${
                  i === index
                    ? 'font-semibold text-sky-800 dark:text-sky-300'
                    : 'text-slate-600 dark:text-slate-400'
                }`}
              >
                <span className="sr-only">
                  {i < index ? 'Đã xong: ' : i === index ? 'Đang ở: ' : ''}
                </span>
                {s}
              </span>
            </li>
          ))}
        </ol>
        <p
          aria-hidden
          className="mt-1 text-base text-slate-700 md:hidden dark:text-slate-300"
        >
          Bước {index + 1}/{JOURNEY_STEPS.length}: {step}
        </p>
      </nav>

      <div className="flex flex-wrap items-center gap-2">
        <h1 ref={heading} tabIndex={-1} className="text-2xl font-bold md:text-3xl">
          {title}
        </h1>
        {demo && <DemoBadge />}
      </div>

      {children}
    </div>
  )
}

/** The source a step comes from, always visible (traceability). */
export function SourceChip({
  sample,
  onOpen,
}: {
  sample: SampleMaterial
  onOpen?: () => void
}): React.JSX.Element {
  const subject = subjectOf(sample)
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-amber-100 px-3 py-2 text-base dark:bg-amber-950/40">
      <span aria-hidden>{subject?.icon ?? '📄'}</span>
      <span className="min-w-0 flex-1">
        <span className="font-semibold">Từ tài liệu: </span>
        {sourceLabel(sample)}
      </span>
      {onOpen !== undefined && (
        <button
          type="button"
          className="min-h-11 px-1 text-sky-800 underline dark:text-sky-300"
          onClick={onOpen}
        >
          Xem tài liệu
        </button>
      )}
    </div>
  )
}

/** A plain-language note that content is DEMO or not connected. */
export function Note({
  children,
  tone = 'demo',
}: {
  children: React.ReactNode
  tone?: 'demo' | 'info'
}): React.JSX.Element {
  return (
    <p
      className={`rounded-xl px-3 py-2 text-base ${
        tone === 'demo'
          ? 'bg-fuchsia-50 text-fuchsia-950 dark:bg-fuchsia-950/40 dark:text-fuchsia-100'
          : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100'
      }`}
    >
      {children}
    </p>
  )
}
